import {
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  Input,
  ViewEncapsulation,
  forwardRef,
  inject,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  NgModel,
} from '@angular/forms';
import { MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { KeyboardStatusService } from '@core/providers/keyboard-status/keyboard-status.service';

@Component({
  selector: 'app-textarea-with-counter',
  templateUrl: './textarea-with-counter.component.html',
  styleUrls: ['./textarea-with-counter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaWithCounterComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatFormField, MatLabel, MatInput, FormsModule, MatIcon, MatSuffix, MatHint],
})
export class TextareaWithCounterComponent implements ControlValueAccessor, AfterViewInit {
  private keyboardStatusService = inject(KeyboardStatusService);
  private inj = inject(Injector);

  readonly maxLength = input<number | undefined>(undefined);
  readonly minHeight = input<number | undefined>(undefined);
  readonly placeholder = input<string>('');
  readonly hint = input<string>('');
  readonly label = input<string>('');
  readonly className = input<string>('');
  readonly iconClass = input<string>();
  readonly iconName = input<string>();
  readonly required = input(false);
  readonly autoFocus = input(false);
  @Input() set hostTouched(value: boolean) {
    this.updatedTouched();
  }

  readonly inputRef = viewChild<NgModel>('inputRef');
  readonly textareaEl = viewChild<ElementRef<HTMLTextAreaElement>>('textareaEl');
  readonly keydown = output<KeyboardEvent>();
  readonly blur = output<void>();

  value: string | undefined;
  counter = 0;
  disabled = false;
  hostControl: NgControl | null = null;
  // Function.prototype is a safe reference that will always point to an empty function. This is only initial, to prevent errors
  private propagateChange: Function = Function.prototype;
  private propagateTouched: Function = Function.prototype;

  ngAfterViewInit(): void {
    this.hostControl = this.inj.get(NgControl, null, { optional: true });
    this.updatedTouched();
    if (this.autoFocus()) {
      const el = this.textareaEl()?.nativeElement;
      if (el) {
        setTimeout(() => {
          el.focus();
          el.select();
        }, 0);
      }
    }
  }

  onChange(newValue: string) {
    this.value = newValue;
    this.updateCounter(newValue);
    this.propagateChange(newValue);
  }

  updateCounter(newValue: string) {
    this.counter = newValue.length;
  }

  writeValue(value: any): void {
    this.value = value ? String(value) : '';
    this.updateCounter(this.value);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleKeydown(event: KeyboardEvent): void {
    this.keydown.emit(event);
  }

  markAsTouched(): void {
    this.keyboardStatusService.setEscModalStatus(true);
    this.propagateTouched();
  }

  onBlur(): void {
    this.markAsTouched();
    this.blur.emit();
  }

  private updatedTouched(): void {
    const inputRef = this.inputRef();
    if (this.hostControl && this.hostControl.touched && inputRef) {
      inputRef.control.markAllAsTouched();
    }
  }
}
